// Durable write queue for offline-first mutations.
//
// Rows live in the IndexedDB 'outbox' store. Each row is a pending mutation
// the user performed locally that still needs to reach the backend. The
// queue is drained on reconnect (or on explicit retry).
//
// Mutation types:
//   'habit.create'        POST   /api/habits          → server assigns id
//   'habit.update'        PUT    /api/habits/:id
//   'habit.delete'        DELETE /api/habits/:id
//   'completion.toggle'   POST   /api/completions     idempotent toggle
//   'sickWeek.toggle'     POST   /api/sick-weeks      idempotent toggle
//
// Temp-id reconciliation: when a 'habit.create' resolves, the locally-assigned
// 'tmp_*' id needs to be swapped for the real server uuid in every still-queued
// mutation that references it. drain() does that walk and emits onIdResolved so
// consumers (hooks) can rewrite their own local stores too.
//
// Dedup on enqueue:
//   - completion.toggle and sickWeek.toggle: if a queued row already exists for
//     the same composite key, the new one *cancels* the old (two toggles = no-op).
//     This keeps the queue bounded under rapid tapping.

import { fetchWithRetry } from '../utils/fetchWithRetry';
import { getAll, put, del, clear } from './db';

const STORE = 'outbox';

function uuid() {
  if (typeof crypto !== 'undefined' && crypto.randomUUID) {
    return crypto.randomUUID();
  }
  return 'r_' + Math.random().toString(36).slice(2) + Date.now().toString(36);
}

export function newTempHabitId() {
  return 'tmp_' + uuid();
}

export function isTempId(id) {
  return typeof id === 'string' && id.startsWith('tmp_');
}

function toggleKey(type, payload) {
  if (type === 'completion.toggle') return `${payload.habit_id}-${payload.date}`;
  if (type === 'sickWeek.toggle') return `${payload.habit_id}-${payload.week_start}`;
  return null;
}

async function findExistingToggle(type, payload) {
  const key = toggleKey(type, payload);
  if (!key) return null;
  const rows = await getAll(STORE);
  return rows.find((r) => r.type === type && toggleKey(r.type, r.payload) === key) || null;
}

// Append a mutation to the queue. Returns the row id, or null if the new
// mutation cancelled a queued one (net no-op).
export async function enqueue(mutation) {
  const existing = await findExistingToggle(mutation.type, mutation.payload);
  if (existing) {
    await del(STORE, existing.id);
    return null;
  }
  const row = {
    id: uuid(),
    type: mutation.type,
    payload: mutation.payload,
    habitRef: mutation.habitRef || null,
    tempId: mutation.tempId || null,
    createdAt: new Date().toISOString(),
    attemptCount: 0,
    lastError: null,
  };
  await put(STORE, row);
  return row.id;
}

export async function peek() {
  const rows = await getAll(STORE);
  return rows.sort((a, b) => (a.createdAt < b.createdAt ? -1 : 1));
}

export async function pendingCount() {
  const rows = await getAll(STORE);
  return rows.length;
}

// Remove every queued mutation referencing the given habit id — used when the
// hook layer deletes a habit (offline-created or otherwise) and wants to
// abandon any pending toggles on it.
export async function removeForHabit(habitId) {
  const rows = await getAll(STORE);
  const dead = rows.filter(
    (r) => r.payload?.habit_id === habitId || r.payload?.id === habitId || r.tempId === habitId
  );
  for (const r of dead) await del(STORE, r.id);
  return dead.length;
}

// Wipe the queue. Called on logout so a queued mutation for user A is never
// sent under user B's token.
export async function clearAll() {
  await clear(STORE);
}

async function rewriteTempId(tempId, realId) {
  const rows = await getAll(STORE);
  const updates = rows.filter((r) => r.habitRef === tempId || r.payload?.habit_id === tempId);
  for (const r of updates) {
    if (r.habitRef === tempId) r.habitRef = realId;
    if (r.payload?.habit_id === tempId) r.payload = { ...r.payload, habit_id: realId };
    await put(STORE, r);
  }
}

function buildRequest(apiBase, token, row) {
  const headers = {
    'Content-Type': 'application/json',
    Authorization: `Bearer ${token}`,
  };
  switch (row.type) {
    case 'habit.create':
      return { url: `${apiBase}/api/habits`, init: { method: 'POST', headers, body: JSON.stringify(row.payload) } };
    case 'habit.update': {
      const { id, ...rest } = row.payload;
      return { url: `${apiBase}/api/habits/${id}`, init: { method: 'PUT', headers, body: JSON.stringify(rest) } };
    }
    case 'habit.delete':
      return { url: `${apiBase}/api/habits/${row.payload.id}`, init: { method: 'DELETE', headers } };
    case 'completion.toggle':
      return { url: `${apiBase}/api/completions`, init: { method: 'POST', headers, body: JSON.stringify(row.payload) } };
    case 'sickWeek.toggle':
      return { url: `${apiBase}/api/sick-weeks`, init: { method: 'POST', headers, body: JSON.stringify(row.payload) } };
    default:
      throw new Error(`outbox: unknown mutation type ${row.type}`);
  }
}

// Drain the queue. Stops on the first transient (network/5xx) failure for a
// given row, but continues on to subsequent rows of different types — only
// a hard 4xx drops the row as permanently rejected.
//
// Returns { succeeded, rejected, deferred } counts.
//
// onIdResolved(tempId, realId, serverHabit) — invoked when a habit.create
// resolves; the hooks layer uses this to rewrite habit_id refs in its own
// local stores (habits, completions, sickWeeks) and React state.
export async function drain({ apiBase, token, onIdResolved, onRejected }) {
  const rows = await peek();
  let succeeded = 0;
  let rejected = 0;
  let deferred = 0;

  for (const row of rows) {
    // Skip rows that still reference an unresolved temp id — the create that
    // would resolve them must be processed first. Since peek() returns in
    // createdAt order and creates are always enqueued before dependents, this
    // is defensive (shouldn't normally trigger).
    if (row.habitRef && isTempId(row.habitRef)) {
      deferred++;
      continue;
    }

    let res;
    try {
      const { url, init } = buildRequest(apiBase, token, row);
      res = await fetchWithRetry(url, init);
    } catch (err) {
      // fetchWithRetry exhausted — transient. Leave in queue for next drain.
      row.attemptCount += 1;
      row.lastError = err.message || String(err);
      await put(STORE, row);
      deferred++;
      continue;
    }

    if (res.ok) {
      if (row.type === 'habit.create' && row.tempId) {
        let serverHabit = null;
        try { serverHabit = await res.json(); } catch (_) { /* ignore */ }
        if (serverHabit && serverHabit.id) {
          await rewriteTempId(row.tempId, serverHabit.id);
          // Also patch the rows we're still iterating — rewriteTempId only
          // touches IDB, but the loop holds an in-memory snapshot taken before
          // the resolve. Without this patch, the next dependent mutation
          // would be sent with the stale tmp_* habit_id and be rejected.
          for (const r of rows) {
            if (r.habitRef === row.tempId) r.habitRef = serverHabit.id;
            if (r.payload && r.payload.habit_id === row.tempId) {
              r.payload = { ...r.payload, habit_id: serverHabit.id };
            }
          }
          if (onIdResolved) {
            try { await onIdResolved(row.tempId, serverHabit.id, serverHabit); }
            catch (_) { /* don't let consumer errors stall the drain */ }
          }
        }
      }
      await del(STORE, row.id);
      succeeded++;
      continue;
    }

    // Non-ok response that wasn't transient (fetchWithRetry already retried
    // 408/425/429/5xx). Treat as permanent rejection: drop and notify.
    rejected++;
    if (onRejected) {
      try { await onRejected(row, res.status); } catch (_) { /* ignore */ }
    }
    await del(STORE, row.id);
  }

  return { succeeded, rejected, deferred };
}
